/**
 * One motion language for the whole site.
 *
 * Every animated thing — CSS transitions, Framer Motion — pulls its easing and
 * duration from here. Letting each surface use its own defaults (Framer's
 * `easeOut` next to a raw CSS `ease`) is what makes a page feel assembled out
 * of parts instead of designed, so these are not meant to be overridden inline.
 */

/** Primary curve: quick departure, long quiet settle. Used for ~90% of motion. */
export const EASE = [0.16, 1, 0.3, 1] as const;

/** For things leaving or collapsing — mirrors EASE so exits feel related. */
export const EASE_IN = [0.7, 0, 0.84, 0] as const;

/**
 * Deliberately off-round. Round numbers (0.3s, 0.5s, 0.8s) are the fingerprint
 * of untuned defaults; these were nudged by eye until each felt right for the
 * size of thing it moves.
 */
export const DUR = {
    /** hover, focus, colour shifts — must feel instant */
    micro: 0.16,
    /** small elements entering */
    quick: 0.34,
    /** the standard scroll reveal */
    base: 0.52,
    /** hero-scale reveals */
    slow: 0.88,
} as const;

/** Gap between siblings in a staggered group. */
export const STAGGER = 0.055;

/** Viewport trigger shared by every scroll reveal, so nothing fires early. */
export const VIEWPORT = { once: true, margin: "-12% 0px -12% 0px" } as const;

/**
 * Whole-route transition — Home ↔ Resume ↔ a future project case study.
 *
 * Deliberately not a slide or a scale: those imply a spatial relationship
 * ('the next page lives to the right of this one'), which isn't true here —
 * routes aren't siblings in a sequence, they're distinct documents. A fade
 * with a small vertical settle reads as 'this view resolved' without
 * asserting a direction, so a route swap feels like the rest of the site
 * arriving rather than a different piece of software cutting in.
 *
 * Paired with `mode="wait"` on AnimatePresence (see App.tsx) so the leaving
 * page fully clears before the next one starts — two pages animating at once
 * is exactly the "rối mắt" (visually busy) result this exists to avoid.
 */
export const PAGE_TRANSITION = {
    initial: { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { duration: DUR.base, ease: EASE },
} as const;

/**
 * Governs the Intro → Home shared-layout flight (see lib/introMorph.tsx):
 * the name/role elements Intro and Home both tag with the same `layoutId`.
 *
 * Framer defaults a `layoutId` handoff to a spring, and — measured directly,
 * not assumed — that spring settles in well under 300ms regardless of what
 * duration a *different* transition prop elsewhere requests, which is far too
 * quick for what's supposed to be the site's one deliberate entrance.
 *
 * The reason this has to be a shared export rather than living next to
 * whichever component happens to define it: the transition that actually
 * governs a `layoutId` handoff can come from either side of the swap
 * depending on Framer's internals, so both Intro's copy and Home's copy have
 * to pass the *identical* object — one of them defining it and the other
 * guessing at a matching number is exactly how these two would quietly drift
 * apart the next time either gets tuned.
 */
export const HERO_FLIP_TRANSITION = { duration: 1.1, ease: EASE } as const;
